import {
  type Dispatch,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react"

import type { TourId } from "../modules/tour-start-store"
import type { Capacity, GameState } from "../sortaballs-8-colors/logic"

import { IS_AUTH } from "../modules/auth"
import { useSettings } from "../modules/settings"
import {
  isTourDismissed,
  markTourDismissed,
  useTourStartRequest,
} from "../modules/tour-start-store"
import {
  canMoveGroup,
  topRunLength,
  validMoves,
} from "../sortaballs-8-colors/logic"

import { RESET_CONFIRM_THRESHOLD } from "./constants"

export { GameplayTourView } from "../shared-components/gameplay-tour-view"

// All sortaballs variants (8-colors, 10-colors, …) share this tour and key.
const TOUR_ID: TourId = "sortaballs"

const SELECT_PAUSE_MS = 600
const FLIGHT_SETTLE_MS = 900
const HOLD_MS = 700
// How long a help-reveal replay of the first correct moves can run before the
// tour advances (fallback when the reveal animation is skipped/fast) — the
// step normally waits for the reveal to finish first.
const HELP_REVEAL_MS = 6000

// The narrated step texts. The tour drives real selections/moves/long-presses
// on the board (the demoSelectPlate/selectAll dispatches run live animations)
// and shows the matching step alongside, so the player sees-and-reads each
// gesture. Step 6 is narration-only (a behavior the player already saw in the
// move demo). Steps 7–9 press the real header buttons: show-correct-moves
// (and waits for its replay animation), undo and reset.
const TOUR_STEPS = [
  "1/9 — Tap a plate to select its top ball. The highlight shows your selection.",
  "2/9 — Select empty plate or plate which top ball has the same color and move it.",
  "3/9 — If you do a LONG HOLD on a plate, it will select ALL top balls of the same color.",
  "4/9 — You can get the same result by tapping the same plate N times.",
  "5/9 — Tapping another plate either makes a move, or — when it can't take the ball — selects that new plate instead (your old pick is dropped).",
  "6/9 — While the balls are still flying to their plate, you can ALREADY tap another plate to mark your next move — you don't have to wait for the flight to end.",
  "7/9 — The lightbulb button shows the next correct moves: press it and watch them play out automatically.",
  "8/9 — Undo reverts your last move.",
  "9/9 — Reset puts the board back to the starting position.",
]

interface TourPlan {
  from: number
  longPressIndex: number
  to: number
}

interface TourState {
  canAdvance: boolean
  isActive: boolean
  // True while a queued start (3+ moves on the board) awaits the player's OK
  // in the confirmation dialog — the tour has not started yet.
  pendingStart: boolean
  stepIndex: number
}

// A single stable move to demo, plus the plate to show long-press select-all.
// Preferring an empty destination for the move (2.2); the long-press plate is
// one whose top run is >= 2, else the first non-empty plate (3.1).
export function buildTourPlan(
  board: GameState,
  capacity: Capacity,
): null | TourPlan {
  const moves = validMoves(board, capacity)
  if (moves.length === 0) {
    return null
  }
  const move =
    moves.find(m=> board[m.to].length === 0 && m.from !== m.to) ?? moves[0]
  const multiRun = board.findIndex(plate=> topRunLength(plate) >= 2)
  const longPressIndex =
    multiRun === -1 ? board.findIndex(plate=> plate.length > 0) : multiRun
  return { from: move.from, longPressIndex, to: move.to }
}

const INITIAL_TOUR_STATE: TourState = {
  canAdvance: false,
  isActive: false,
  pendingStart: false,
  stepIndex: -1,
}

interface GameplayTourInput {
  board: GameState
  capacity: Capacity
  clearSelection: ()=> void
  // Header-button demos (steps 6–8): the SAME onClick handlers the header's
  // real help/undo/reset buttons run — the tour presses the actual button, it
  // never calls a parallel helper, so any button side effect fires exactly as
  // for a real click (the help step commits its revealed moves, which the
  // closing undo/reset steps then roll back). `demoReset`
  // also runs when the tour STARTS — the demo gestures need the puzzle's
  // starting board (a mid-game or solved board may offer no legal move, which
  // would end the tour instantly).
  demoHelp?: ()=> void
  demoReset?: ()=> void
  demoSelectPlate: (index: number)=> void
  demoUndo?: ()=> void
  // Battle mode (challenges) has no guided tour.
  disabled?: boolean
  // True while the help-reveal replay animates (the game's isPlaying) — the
  // show-moves step waits for it to finish before enabling Continue.
  isRevealPlaying?: boolean
  // Committed moves on the current board. Starting the tour resets the board,
  // so >= RESET_CONFIRM_THRESHOLD moves requires the player's confirmation
  // first (same rule as the header's reset button).
  moveCount: number
  selectAll: (index: number)=> void
}

// Header button the current step points at (steps 7–9 press the real help,
// undo and reset buttons); null for board-gesture and narration steps.
export type TourButtonTarget = null | "help" | "reset" | "undo"

interface GameplayTourResult {
  canAdvance: boolean
  // Reject a pending start-tour confirmation: the board stays untouched and
  // the tour is NOT dismissed, so the sidebar button can ask again.
  cancelStart: ()=> void
  close: ()=> void
  confirmStart: ()=> void
  continue: ()=> void
  // Amber ring on the header button the current step teaches (rendered by the
  // game's header via the `tour-highlight` class).
  highlightButton: TourButtonTarget
  holdCueIndex: number
  isActive: boolean
  // Committed moves — shown in the start-tour confirmation copy.
  moveCount: number
  // True while a queued start (3+ moves on the board) awaits the player's OK.
  pendingStartConfirm: boolean
  stepText: null | string
}

// Map the 0-based step (TOUR_STEPS) to the header button it teaches.
function buttonTargetForStep(stepIndex: number): TourButtonTarget {
  if (stepIndex === 6) return "help"
  if (stepIndex === 7) return "undo"
  if (stepIndex === 8) return "reset"
  return null
}

interface PerformStepContext {
  board: GameState
  capacity: Capacity
  clearSelection: ()=> void
  demoHelp?: ()=> void
  demoReset?: ()=> void
  demoSelectPlate: (index: number)=> void
  demoUndo?: ()=> void
  dispatch: Dispatch<TourAction>
  // Latest reveal state — the help step polls it until replay finishes.
  isRevealPlaying: ()=> boolean
  schedule: (fn: ()=> void, ms: number)=> void
  selectAll: (index: number)=> void
  // -1 clears the on-plate "HOLD" cue.
  setHoldCue: (index: number)=> void
}

type TourAction =
  | { type: "CANADVANCE"; value: boolean }
  | { type: "END" }
  | { type: "NEXT" }
  | { type: "PENDING_START" }
  | { type: "START" }

// Sortaballs' auto-playing gameplay tour. Starts on mount for non-auth users
// who haven't dismissed, and re-runs whenever the sidebar "Start Tour" button
// fires (requestTourStart). Each step performs its gesture on the real board.
export function useGameplayTour(input: GameplayTourInput): GameplayTourResult {
  const {
    board,
    capacity,
    clearSelection,
    demoHelp,
    demoReset,
    demoSelectPlate,
    demoUndo,
    disabled = false,
    isRevealPlaying = false,
    moveCount,
    selectAll,
  } = input
  const [state, dispatch] = useReducer(tourReducer, INITIAL_TOUR_STATE)
  const autoStartedRef = useRef(false)
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())
  const tourRequest = useTourStartRequest()
  const prevRequestRef = useRef(tourRequest)
  const { isVisible } = useSettings()
  const [holdCueIndex, setHoldCueIndex] = useState(-1)
  // `disabled` gates an event-time decision (the sidebar Start Tour request)
  // and must be read without re-running the receiving effect on every render —
  // same latest-in-ref pattern as ctxRef below.
  const disabledRef = useRef(disabled)
  disabledRef.current = disabled
  // The reset handler the tour also fires at start — read through a ref so
  // `start` stays stable (callers pass inline arrows with fresh identities).
  const demoResetRef = useRef(demoReset)
  demoResetRef.current = demoReset
  // The help-reveal step polls this until the replay animation stops, so it
  // must always hold the LATEST value (a step-snapshot would go stale).
  const revealPlayingRef = useRef(isRevealPlaying)
  revealPlayingRef.current = isRevealPlaying
  // Latest committed-move count for the start-confirmation gate and dialog
  // copy — read at event time without re-running the effects on every move.
  const moveCountRef = useRef(moveCount)
  moveCountRef.current = moveCount
  const setHoldCue = useCallback((index: number)=> {
    setHoldCueIndex(index)
  }, [])

  const schedule = useCallback((fn: ()=> void, ms: number)=> {
    const id = setTimeout(()=> {
      timersRef.current.delete(id)
      fn()
    }, ms)
    timersRef.current.add(id)
  }, [])

  // Latest values read by the step effect without re-running it on every
  // render: the game's handlers are inline arrows (new identities each
  // render), so depending on them directly would re-execute the current step
  // after every dispatch — an infinite re-render loop ("Maximum update depth
  // exceeded"). Mirrors the select-correct-word tour's ctxRef pattern.
  const ctxRef = useRef<PerformStepContext>({
    board,
    capacity,
    clearSelection,
    demoHelp,
    demoReset,
    demoSelectPlate,
    demoUndo,
    dispatch,
    isRevealPlaying: ()=> revealPlayingRef.current,
    schedule,
    selectAll,
    setHoldCue,
  })
  ctxRef.current = {
    board,
    capacity,
    clearSelection,
    demoHelp,
    demoReset,
    demoSelectPlate,
    demoUndo,
    dispatch,
    isRevealPlaying: ()=> revealPlayingRef.current,
    schedule,
    selectAll,
    setHoldCue,
  }

  const teardown = useCallback(()=> {
    for (const id of timersRef.current) {
      clearTimeout(id)
    }
    timersRef.current.clear()
  }, [])
  useEffect(()=> teardown, [teardown])

  const close = useCallback(()=> {
    markTourDismissed(TOUR_ID)
    teardown()
    setHoldCue(-1)
    // Undo the tour's demo selection so no plate stays highlighted after close.
    clearSelection()
    dispatch({ type: "END" })
  }, [clearSelection, dispatch, setHoldCue, teardown])

  // Opening the settings sidebar dismisses the running tour: the player left
  // the guide to change settings, so stop it and record the dismissal (same
  // as pressing its Close button) — the tour won't auto-start next visit.
  // The sidebar "Start Tour" button closes the sidebar first (real click on
  // its dismiss arrow), so the tour never fires while the sidebar is open.
  useEffect(()=> {
    if (state.isActive && isVisible) {
      close()
    }
  }, [close, isVisible, state.isActive])

  const start = useCallback(()=> {
    // Rule: a started tour is never auto-started again — mark it at start so
    // a mid-tour reload or re-mount can't replay it. (Manual sidebar starts
    // force-run below regardless of the flag.)
    markTourDismissed(TOUR_ID)
    teardown()
    // Demo a CLEAN board: the narrated gestures (buildTourPlan) need at least
    // one legal move, which any fresh puzzle has but a mid-game or solved
    // board may not. Pressing the game's real reset handler also clears any
    // saved session, so the tour's demo moves replay from a known start.
    demoResetRef.current?.()
    dispatch({ type: "START" })
  }, [dispatch, teardown])

  // The player approved a queued start (board had 3+ moves): run the real
  // start, which resets the board first.
  const confirmStart = useCallback(()=> {
    start()
  }, [start])

  // The player declined: drop the queued start without touching the board or
  // dismissing the tour, so the sidebar button can ask again later.
  const cancelStart = useCallback(()=> {
    dispatch({ type: "END" })
  }, [dispatch])

  // Execute the current step's gesture whenever the tour advances. Deps are
  // stable: everything that can change is read through ctxRef.
  useEffect(()=> {
    if (!state.isActive) {
      return
    }
    performStep(ctxRef.current, state.stepIndex)
  }, [state.isActive, state.stepIndex])

  // Auto-start: non-auth, not dismissed, and not battle mode — and only once
  // the game is visible. On a prod-mobile first visit the sidebar starts open
  // and covers the board, so the tour must wait until the guest closes it
  // ("after the user sees the game"); starting over the open sidebar would
  // teach a game they can't see. The pending start is cancellable, so an open
  // inside the delay holds off and re-arms once the sidebar closes.
  useEffect(()=> {
    if (
      IS_AUTH ||
      disabled ||
      autoStartedRef.current ||
      isTourDismissed(TOUR_ID)
    ) {
      return
    }
    if (isVisible) {
      return
    }
    const id = setTimeout(()=> {
      autoStartedRef.current = true
      // A restored session can remount mid-game; never silently reset a board
      // with real progress — ask first, same as the header's reset button.
      if (moveCountRef.current >= RESET_CONFIRM_THRESHOLD) {
        dispatch({ type: "PENDING_START" })
        return
      }
      start()
    }, 400)
    return ()=> clearTimeout(id)
  }, [disabled, dispatch, isVisible, start])

  // Manual trigger from the sidebar: re-run even if already seen. A board with
  // 3+ moves must be confirmed first — the tour start resets it. (Deferred a
  // tick like start, so the sidebar's own close settles before the dialog.)
  useEffect(()=> {
    if (tourRequest === prevRequestRef.current) {
      return
    }
    prevRequestRef.current = tourRequest
    autoStartedRef.current = true
    if (IS_AUTH || disabledRef.current) {
      return
    }
    if (moveCountRef.current >= RESET_CONFIRM_THRESHOLD) {
      schedule(()=> dispatch({ type: "PENDING_START" }), 0)
      return
    }
    schedule(start, 0)
  }, [dispatch, schedule, start, tourRequest])

  const advance = useCallback(()=> dispatch({ type: "NEXT" }), [])

  return {
    canAdvance: state.canAdvance,
    cancelStart,
    close,
    confirmStart,
    continue: advance,
    highlightButton: buttonTargetForStep(state.stepIndex),
    holdCueIndex,
    isActive: state.isActive,
    moveCount,
    pendingStartConfirm: state.pendingStart,
    stepText:
      state.isActive &&
      state.stepIndex >= 0 &&
      state.stepIndex < TOUR_STEPS.length
        ? TOUR_STEPS[state.stepIndex]
        : null,
  }
}

// Execute the gesture for the current step. Reads the latest `board`/`capacity`
// and the move plan derived from the board as it stands (selection doesn't
// mutate the board, so the plan stays stable across the select→move pair).
function performStep(ctx: PerformStepContext, idx: number) {
  const {
    board,
    capacity,
    clearSelection,
    demoHelp,
    demoReset,
    demoSelectPlate,
    demoUndo,
    dispatch,
    schedule,
    selectAll,
    setHoldCue,
  } = ctx

  if (idx < 0 || idx >= TOUR_STEPS.length) {
    markTourDismissed(TOUR_ID)
    dispatch({ type: "END" })
    return
  }

  const plan = buildTourPlan(board, capacity)
  const ready = ()=> dispatch({ type: "CANADVANCE", value: true })

  if (idx === 0) {
    if (!plan) {
      markTourDismissed(TOUR_ID)
      dispatch({ type: "END" })
      return
    }
    clearSelection()
    demoSelectPlate(plan.from)
    schedule(ready, SELECT_PAUSE_MS)
    return
  }

  if (idx === 1) {
    if (!plan) {
      markTourDismissed(TOUR_ID)
      dispatch({ type: "END" })
      return
    }
    demoSelectPlate(plan.to)
    schedule(ready, FLIGHT_SETTLE_MS)
    return
  }

  if (idx === 2) {
    const multiRun = board.findIndex(plate=> topRunLength(plate) >= 2)
    const target =
      multiRun === -1 ? board.findIndex(plate=> plate.length > 0) : multiRun
    if (target === -1) {
      ready()
      return
    }
    // Teach the long-press gesture, not just its effect: tap one ball, hold
    // (a "HOLD" cue on the plate), then select the whole same-color run.
    clearSelection()
    demoSelectPlate(target)
    setHoldCue(target)
    schedule(()=> {
      selectAll(target)
      setHoldCue(-1)
      schedule(ready, SELECT_PAUSE_MS)
    }, HOLD_MS)
    return
  }

  if (idx === 3) {
    ready()
    return
  }

  // idx === 4 — destination rule: tapping another plate either makes a move
  // or, when it can't take the ball, selects that new plate (old pick dropped).
  // Prefer a non-empty, illegal target so the selection visibly jumps to the
  // new plate; fall back to an empty plate (which clears the selection).
  if (idx === 4) {
    clearSelection()
    const source = board.findIndex(plate=> plate.length > 0)
    if (source === -1) {
      ready()
      return
    }
    demoSelectPlate(source)
    schedule(()=> {
      const blocked = board.findIndex(
        (plate, i)=>
          i !== source &&
          plate.length > 0 &&
          !canMoveGroup({
            capacity,
            count: 1,
            move: { from: source, to: i },
            state: board,
          }),
      )
      const tap =
        blocked !== -1
          ? blocked
          : board.findIndex((plate, i)=> i !== source && plate.length === 0)
      demoSelectPlate(tap === -1 ? (source + 1) % board.length : tap)
      schedule(ready, SELECT_PAUSE_MS)
    }, 250)
    return
  }

  // idx === 5 — narration-only: while a flight is airborne you can already tap
  // another plate to mark your next move (a move can never commit mid-flight;
  // the tap just becomes your new selection). The player saw this gesture in
  // the move demo — this step only points the behavior out.
  if (idx === 5) {
    ready()
    return
  }

  // idx === 6 — the lightbulb header button reveals the next correct moves and
  // replays them with the normal flight animation. Press it (the game's own
  // handler) and hold Continue until the replay has finished animating, so the
  // player sees the moves play out before the tour moves on. Falls back after
  // HELP_REVEAL_MS so a skipped/fast reveal can't stall the tour.
  if (idx === 6) {
    if (!demoHelp) {
      ready()
      return
    }
    clearSelection()
    demoHelp()
    const startedAt = Date.now()
    const poll = ()=> {
      if (!ctx.isRevealPlaying() || Date.now() - startedAt >= HELP_REVEAL_MS) {
        ready()
        return
      }
      schedule(poll, 200)
    }
    schedule(poll, 200)
    return
  }

  // idx === 7 — undo reverts the last move; the game's real handler runs, so
  // the board visibly steps back (the earlier reveal left real history).
  if (idx === 7) {
    if (demoUndo) {
      demoUndo()
    }
    schedule(ready, SELECT_PAUSE_MS)
    return
  }

  // idx === 8 — reset restores the starting board via the real handler.
  if (idx === 8) {
    if (demoReset) {
      demoReset()
    }
    schedule(ready, SELECT_PAUSE_MS)
    return
  }
}

function tourReducer(state: TourState, action: TourAction): TourState {
  switch (action.type) {
    case "CANADVANCE":
      return { ...state, canAdvance: action.value }
    case "END":
      return INITIAL_TOUR_STATE
    case "NEXT":
      return {
        ...state,
        canAdvance: false,
        stepIndex: state.stepIndex + 1,
      }
    case "PENDING_START":
      return { canAdvance: false, isActive: false, pendingStart: true, stepIndex: -1 }
    case "START":
      return { canAdvance: false, isActive: true, pendingStart: false, stepIndex: 0 }
    default:
      return state
  }
}
